import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { errorText } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useCreateHealthCheck,
  useTestHealthCheck,
  useUpdateHealthCheck,
  type HealthCheck,
  type HealthCheckCreate,
} from '@/lib/health-checks'
import { useServices } from '@/lib/cluster'

interface Props {
  open: boolean
  initial?: HealthCheck
  onClose: () => void
}

export function HealthCheckModal({ open, initial, onClose }: Props) {
  const { data: services } = useServices()
  const create = useCreateHealthCheck()
  const update = useUpdateHealthCheck()
  const test = useTestHealthCheck()

  const [serviceId, setServiceId] = useState(initial?.service_id ?? '')
  const [name, setName] = useState(initial?.name ?? 'health')
  const [method, setMethod] =
    useState<HealthCheckCreate['method']>(initial?.method ?? 'GET')
  const [port, setPort] = useState<string>(initial?.port?.toString() ?? '')
  const [urlPath, setUrlPath] = useState(initial?.url_path ?? '/health')
  const [emin, setEmin] = useState(initial?.expect_min ?? 200)
  const [emax, setEmax] = useState(initial?.expect_max ?? 299)
  const [interval, setInterval] = useState(initial?.interval_s ?? 30)
  const [timeout, setTimeout] = useState(initial?.timeout_s ?? 5)
  const [authName, setAuthName] = useState(initial?.auth_header_name ?? '')
  const [authValue, setAuthValue] = useState('')
  const [enabled, setEnabled] = useState(initial?.enabled ?? true)
  const [testResult, setTestResult] = useState<string | null>(null)

  useEffect(() => {
    if (!open) setTestResult(null)
  }, [open])

  if (!open) return null

  const body: HealthCheckCreate = {
    service_id: serviceId,
    name,
    method,
    port: port ? parseInt(port, 10) : null,
    url_path: urlPath,
    expect_min: emin,
    expect_max: emax,
    interval_s: interval,
    timeout_s: timeout,
    auth_header_name: authName || null,
    auth_header_value: authValue || (initial?.auth_header_set ? undefined : null),
    enabled,
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>{initial ? 'Edit health check' : 'New health check'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!initial && (
            <div className="space-y-1">
              <Label>Service</Label>
              <select
                className="w-full rounded border bg-background p-2"
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
              >
                <option value="">— pick a service —</option>
                {services?.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1 col-span-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Method</Label>
              <select
                className="w-full rounded border bg-background p-2"
                value={method}
                onChange={(e) =>
                  setMethod(e.target.value as HealthCheckCreate['method'])
                }
              >
                <option>GET</option><option>POST</option><option>HEAD</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Port</Label>
              <Input value={port} onChange={(e) => setPort(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Path</Label>
              <Input value={urlPath} onChange={(e) => setUrlPath(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="space-y-1">
              <Label>Min code</Label>
              <Input
                type="number"
                value={emin}
                onChange={(e) => setEmin(parseInt(e.target.value || '200', 10))}
              />
            </div>
            <div className="space-y-1">
              <Label>Max code</Label>
              <Input
                type="number"
                value={emax}
                onChange={(e) => setEmax(parseInt(e.target.value || '299', 10))}
              />
            </div>
            <div className="space-y-1">
              <Label>Interval (s)</Label>
              <Input
                type="number"
                value={interval}
                onChange={(e) => setInterval(parseInt(e.target.value || '30', 10))}
              />
            </div>
            <div className="space-y-1">
              <Label>Timeout (s)</Label>
              <Input
                type="number"
                value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value || '5', 10))}
              />
            </div>
          </div>

          <div className="space-y-2 border-t pt-3">
            <Label className="font-medium">Auth (optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Header name (X-API-Key)"
                value={authName}
                onChange={(e) => setAuthName(e.target.value)}
              />
              <Input
                type="password"
                placeholder={
                  initial?.auth_header_set
                    ? 'Already set, leave empty to keep'
                    : 'Header value'
                }
                value={authValue}
                onChange={(e) => setAuthValue(e.target.value)}
              />
            </div>
            {initial?.auth_header_set && (
              <button
                type="button"
                className="text-xs text-destructive underline"
                onClick={() => { setAuthName(''); setAuthValue('') }}
              >Clear (set to none)</button>
            )}
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>

          {testResult && (
            <div className="text-xs font-mono rounded bg-muted p-2">
              {testResult}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button
              variant="outline"
              disabled={test.isPending || !serviceId || !port}
              onClick={async () => {
                try {
                  const r = await test.mutateAsync(body)
                  setTestResult(
                    `status=${r.status_code ?? 'err'} · ${r.duration_ms}ms · ${r.error_text ?? 'OK'}`
                  )
                } catch (e) {
                  setTestResult(
                    `Test failed: ${e instanceof Error ? e.message : 'unknown'}`
                  )
                }
              }}
            >Test now</Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button
                disabled={create.isPending || update.isPending || !serviceId}
                onClick={async () => {
                  // Without this, a rejected save (403 on a readonly key,
                  // validation error) rejects unhandled: the dialog just sits
                  // there and the user thinks nothing happened.
                  try {
                    if (initial) {
                      await update.mutateAsync({ id: initial.id, body })
                    } else {
                      await create.mutateAsync(body)
                    }
                    toast.success(initial ? 'Health check updated' : 'Health check created')
                    onClose()
                  } catch (e) {
                    toast.error(errorText(e, 'Save failed'))
                  }
                }}
              >Save</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
